import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";
import OwnerInfo from "./OwnerInfo";
import "../pages/CompaniesPage.css";

const INDUSTRIES = [
  "Technology",
  "Finance",
  "Healthcare",
  "Retail",
  "Manufacturing",
  "Real Estate",
  "Education",
  "Telecommunications",
  "Energy",
  "Transportation",
  "Hospitality",
  "Media & Entertainment",
  "Legal Services",
  "Consulting",
  "Insurance",
  "Agriculture",
  "Construction",
  "Government",
  "Non-profit",
  "Other",
];

const COMPANY_SIZES = [
  "1-10 employees",
  "11-50 employees",
  "51-100 employees",
  "101-500 employees",
  "501-1,000 employees",
  "1,000-5,000 employees",
  "5,000-10,000 employees",
  "10,000+ employees",
];

const companySchema = z.object({
  name: z.string()
    .min(1, "Company name is required")
    .min(2, "Company name must be at least 2 characters")
    .max(100, "Company name cannot exceed 100 characters"),
  industry: z.string()
    .min(1, "Industry is required"),
  location: z.string()
    .min(1, "Location is required"),
  company_size: z.string()
    .min(1, "Company size is required"),
  website: z.string()
    .refine(
      (value) => !value || /^(https?:\/\/)?.+\..+/i.test(value),
      "Please enter a valid website URL"
    )
    .optional()
    .or(z.literal("")),
  description: z.string()
    .max(500, "Description cannot exceed 500 characters")
    .optional()
    .or(z.literal("")),
  collab_ids: z.array(z.string()).optional(),
});

export default function AddCompanyModal({ onClose, onSuccess, companyToEdit }) {
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(companySchema),
    mode: "onChange",
    defaultValues: {
      name: companyToEdit?.name || "",
      description: companyToEdit?.description || "",
      website: companyToEdit?.website || "",
      industry: companyToEdit?.industry || "",
      location: companyToEdit?.location || "",
      company_size: companyToEdit?.company_size || "",
      collab_ids: companyToEdit?.collab_ids || [],
    },
  });

  const formValues = watch();

  const handleClose = () => {
    if (!saving) {
      setApiError("");
      reset();
      onClose();
    }
  };

  const onSubmit = async (data) => {
    setApiError("");
    try {
      setSaving(true);
      const payload = {
        ...data,
        collab_ids: Array.isArray(data.collab_ids) ? data.collab_ids : (data.collab_ids ? [data.collab_ids] : []),
      };
      if (companyToEdit) {
        await api.put(`/api/companies/${companyToEdit.id}/`, payload);
      } else {
        await api.post("/api/companies/", payload);
      }
      onSuccess();
      reset();
    } catch (err) {
      console.error(err);
      setApiError("Failed to save company. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const [users, setUsers] = useState([]);
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const res = await api.get('/api/admin/users/');
        setUsers(res.data || []);
      } catch (err) {
        // ignore - dropdown will simply not show
      }
    };
    loadUsers();
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          width: "640px",
          maxHeight: "90vh",
          overflowY: "auto",
          padding: "2rem 2.5rem",
          boxShadow: "0 25px 50px rgba(15,23,42,0.35)",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.5rem",
          }}
        >
          <h2 style={{ fontSize: "1.6rem", fontWeight: "700" }}>
            {companyToEdit ? "Edit Company" : "Add New Company"}
          </h2>
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "1.3rem",
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.6 : 1,
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <OwnerInfo user={user} />

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem 1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <div>
              <label className={`form-label ${errors.name ? "label-error" : ""}`}>
                Name *
              </label>
              <input
                type="text"
                {...register("name")}
                placeholder="Enter company name"
                className={`form-input ${errors.name ? "input-error" : ""}`}
              />
              {errors.name && (
                <div className="field-error">
                  {errors.name.message}
                </div>
              )}
              <div className="char-count">{(formValues.name || "").length}/100</div>
            </div>

            <div>
              <label className={`form-label ${errors.industry ? "label-error" : ""}`}>
                Industry *
              </label>
              <select
                {...register("industry")}
                className={`form-select ${errors.industry ? "input-error" : ""}`}
              >
                <option value="">Select industry</option>
                {INDUSTRIES.map((industry) => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
              {errors.industry && (
                <div className="field-error">
                  {errors.industry.message}
                </div>
              )}
            </div>

            <div>
              <label className={`form-label ${errors.location ? "label-error" : ""}`}>
                Location *
              </label>
              <input
                type="text"
                {...register("location")}
                placeholder="e.g., New York, USA"
                className={`form-input ${errors.location ? "input-error" : ""}`}
              />
              {errors.location && (
                <div className="field-error">
                  {errors.location.message}
                </div>
              )}
              <div className="char-count">{(formValues.location || "").length}/100</div>
            </div>

            <div>
              <label className={`form-label ${errors.company_size ? "label-error" : ""}`}>
                Company Size *
              </label>
              <select
                {...register("company_size")}
                className={`form-select ${errors.company_size ? "input-error" : ""}`}
              >
                <option value="">Select size</option>
                {COMPANY_SIZES.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              {errors.company_size && (
                <div className="field-error">
                  {errors.company_size.message}
                </div>
              )}
            </div>

            <div>
              <label className={`form-label ${errors.website ? "label-error" : ""}`}>
                Website
              </label>
              <input
                type="url"
                {...register("website")}
                placeholder="https://example.com"
                className={`form-input ${errors.website ? "input-error" : ""}`}
              />
              {errors.website && (
                <div className="field-error">
                  {errors.website.message}
                </div>
              )}
              <div className="char-count">{(formValues.website || "").length}/200</div>
            </div>
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label className={`form-label ${errors.description ? "label-error" : ""}`}>
              Description
            </label>
            <textarea
              {...register("description")}
              placeholder="Add detailed description..."
              rows={4}
              className={`form-textarea ${errors.description ? "input-error" : ""}`}
            />
            {errors.description && (
              <div className="field-error">
                {errors.description.message}
              </div>
            )}
            <div className="char-count">{(formValues.description || "").length}/500</div>
          </div>

          {/* Collab dropdown (optional) */}
          <div style={{ marginBottom: "1.5rem" }}>
            <label className={`form-label`}>Collaborators</label>
            <select {...register("collab_ids")} className={`form-select`} multiple>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.username || u.email}</option>
              ))}
            </select>
            <small>Select one or more collaborators (Ctrl+Click or Cmd+Click)</small>
          </div>

          {apiError && (
            <div
              style={{
                background: "#fee2e2",
                border: "1px solid #fecaca",
                color: "#b91c1c",
                fontSize: "13px",
                marginBottom: "1rem",
                padding: "12px",
                borderRadius: "8px",
              }}
            >
              ⚠ {apiError}
            </div>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.75rem",
              marginTop: "1rem",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                background: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                minWidth: "100px",
                opacity: saving ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || Object.keys(errors).length > 0}
              style={{
                padding: "10px 18px",
                borderRadius: "8px",
                border: "none",
                background: Object.keys(errors).length > 0 ? "#9ca3af" : "#2563eb",
                color: "#fff",
                fontWeight: "600",
                cursor: saving ? "not-allowed" : "pointer",
                minWidth: "130px",
                opacity: saving ? 0.7 : 1,
                transition: "background-color 0.2s",
              }}
            >
              {saving ? "Saving..." : companyToEdit ? "Update Company" : "Save Company"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
